// agentCallAdmin / dto — the wire contract of the operator's "AI usage"
// views over the agent-call ledger (#86). The zod schemas ARE the contract:
// the server parses the query with `aiUsageQuery` and answers with the
// response shapes; the client parses them at its boundary
// (`@bilda/app/agentCallAdmin`). Browser-safe: zod only.
//
// Facts, never display text: instants are ISO timestamps, days are ISO
// dates, money is integer micro-USD (null = unpriced, never 0), durations
// are milliseconds. A call site or outcome this code does not know decodes
// to an explicit unknown case, never a parse failure.
export { AI_USAGE_PAGE_SIZE, aiUsageBreakdownBy, aiUsageCallDetailDto, aiUsageCallListDto, aiUsageCostFilters, aiUsageOutcomeDto, aiUsageOutcomeFilters, aiUsageQuery, aiUsageRatesDto, aiUsageSiteDto, aiUsageSiteTypes, aiUsageSorts, aiUsageSummaryDto, };
import { z } from "zod/v4";
const AI_USAGE_PAGE_SIZE = 25;
// === Query (the filters every view shares) ===
const aiUsageSiteTypes = [
    "chat_round",
    "ocr_batch",
    "source_understanding",
    "part_generation",
    "judged_checks",
    "claim_reading",
    "material_repair",
    "journal_summary",
    "activity_summary",
    "material_draft",
    "freeform_structuring",
];
// How a call ended, as a filter: one value per ledger outcome, plus
// "not completed" (everything but a whole reply).
const aiUsageOutcomeFilters = [
    "completed",
    "partial",
    "failed",
    "cancelled",
    "not_completed",
];
const aiUsageCostFilters = [
    "unpriced",
    "at_least_10c",
    "at_least_1usd",
];
const aiUsageSorts = ["time", "cost", "tokens", "duration", "ttft"];
const aiUsageBreakdownBy = ["workspace", "user", "model", "call_type"];
const optionalText = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .optional()
    .catch(undefined);
// Query-string values: every field optional except the period. An
// unknown filter value is dropped (`catch`), never an error: a stale
// link still opens.
const aiUsageQuery = z.object({
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }),
    user: optionalText,
    workspace: optionalText,
    callType: z.enum(aiUsageSiteTypes).optional().catch(undefined),
    model: optionalText,
    outcome: z.enum(aiUsageOutcomeFilters).optional().catch(undefined),
    cost: z.enum(aiUsageCostFilters).optional().catch(undefined),
    // Any id a call used: an entity id, a file content hash, or a tool name.
    entity: optionalText,
    file: optionalText,
    tool: optionalText,
    // Free search: a call id, a vendor request id or a conversation id.
    q: optionalText,
    sort: z.enum(aiUsageSorts).optional().catch(undefined),
    dir: z.enum(["asc", "desc"]).optional().catch(undefined),
    offset: z.coerce.number().int().min(0).optional().catch(undefined),
    by: z.enum(aiUsageBreakdownBy).optional().catch(undefined),
});
// === Call site and outcome (decoded leniently) ===
const knownSite = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("chat_round"),
        conversationId: z.string(),
        turnMessageId: z.string(),
        round: z.number(),
        roundKind: z.string(),
        opener: z.string(),
    }),
    z.object({
        type: z.literal("ocr_batch"),
        contentHash: z.string(),
        pages: z.array(z.number()),
    }),
    z.object({
        type: z.literal("source_understanding"),
        resourceId: z.string(),
        mode: z.string(),
        pass: z.object({ type: z.string() }),
    }),
    z.object({
        type: z.literal("part_generation"),
        rootId: z.string(),
        partId: z.string(),
        proposal: z.number(),
    }),
    z.object({
        type: z.literal("judged_checks"),
        rootId: z.string(),
        partIds: z.array(z.string()),
    }),
    z.object({
        type: z.literal("claim_reading"),
        rootId: z.string(),
        sourceId: z.string(),
    }),
    z.object({
        type: z.literal("material_repair"),
        sourceId: z.string(),
        iteration: z.number(),
    }),
    z.object({
        type: z.literal("journal_summary"),
        period: z.string(),
        job: z.string(),
    }),
    z.object({ type: z.literal("activity_summary") }),
    z.object({ type: z.literal("material_draft"), sourceId: z.string() }),
    z.object({ type: z.literal("freeform_structuring"), attempt: z.number() }),
]);
const unknownSite = z.object({
    type: z.literal("unknown_site"),
    label: z.string(),
});
const aiUsageSiteDto = z.union([knownSite, unknownSite]).catch((ctx) => ({
    type: "unknown_site",
    label: labelOf(ctx.input),
}));
const stopDto = z.union([
    z.object({
        type: z.literal("stop_known"),
        reason: z.string(),
        raw: z.string(),
    }),
    z.object({ type: z.literal("stop_unknown"), raw: z.string() }),
]);
const knownOutcome = z.discriminatedUnion("type", [
    z.object({ type: z.literal("call_completed"), stop: stopDto }),
    z.object({ type: z.literal("call_partial") }),
    z.object({
        type: z.literal("call_failed"),
        error: z.string(),
        httpStatus: z.number().optional(),
    }),
    z.object({ type: z.literal("call_cancelled") }),
]);
const aiUsageOutcomeDto = z
    .union([
    knownOutcome,
    z.object({ type: z.literal("outcome_unknown"), raw: z.string() }),
])
    .catch((ctx) => ({
    type: "outcome_unknown",
    raw: labelOf(ctx.input),
}));
// The `type` of an unknown wire value, for an explicit unknown case.
function labelOf(value) {
    return typeof value === "object" &&
        value !== null &&
        "type" in value &&
        typeof value.type === "string"
        ? value.type
        : "unknown";
}
// === Shared pieces ===
const party = z.object({ id: z.string(), label: z.string().nullable() });
const totalsDto = z.object({
    calls: z.number(),
    failed: z.number(),
    partial: z.number(),
    cancelled: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    cacheReadTokens: z.number(),
    cacheWriteTokens: z.number(),
    costMicroUsd: z.number(),
    unpricedCalls: z.number(),
    medianDurationMs: z.number().nullable(),
    p95DurationMs: z.number().nullable(),
    medianTtftMs: z.number().nullable(),
});
// === Overview ===
const dayDto = z.object({
    day: z.iso.date(),
    calls: z.number(),
    tokens: z.number(),
    costMicroUsd: z.number(),
});
const breakdownDto = z.object({
    by: z.enum(aiUsageBreakdownBy),
    rows: z.array(z.object({
        // null = calls with no value here (no user, no workspace).
        key: z.string().nullable(),
        label: z.string().nullable(),
        calls: z.number(),
        tokens: z.number(),
        costMicroUsd: z.number(),
    })),
    totalRows: z.number(),
});
const aiUsageSummaryDto = z.object({
    totals: totalsDto,
    // The same span just before `from`, for the cost comparison.
    previousCostMicroUsd: z.number(),
    days: z.array(dayDto),
    breakdown: breakdownDto,
    // Whether the ledger holds any call at all (the "no calls yet" state).
    anyCalls: z.boolean(),
    // Ledger entries this server process dropped (recorder down).
    droppedSinceStart: z.number(),
});
// === Calls ===
const callRowDto = z.object({
    callId: z.string(),
    startedAt: z.iso.datetime({ offset: true }),
    site: aiUsageSiteDto,
    attempt: z.number(),
    initiatorType: z.string(),
    user: party.nullable(),
    workspace: party.nullable(),
    modelId: z.string(),
    usageType: z.string(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    cacheReadTokens: z.number(),
    cacheWriteTokens: z.number(),
    costMicroUsd: z.number().nullable(),
    ttftMs: z.number().nullable(),
    durationMs: z.number(),
    outcome: aiUsageOutcomeDto,
    vendorRequestId: z.string().nullable(),
    // Tools the call's round ran (chat rounds only; 0 otherwise).
    toolCalls: z.number(),
});
const aiUsageCallListDto = z.object({
    rows: z.array(callRowDto),
    total: z.number(),
    totals: totalsDto,
    // What the filter menus offer: every user, workspace and model with a
    // call in the period (other filters ignored).
    facets: z.object({
        users: z.array(party),
        workspaces: z.array(party),
        models: z.array(z.string()),
    }),
});
// === Call detail ===
const entityDto = z.object({
    type: z.string(),
    id: z.string(),
    role: z.string(),
    // Resolved from the workspace's log; null when unknown or not titled.
    title: z.string().nullable(),
});
const fileDto = z.object({
    contentHash: z.string(),
    pages: z.array(z.object({ first: z.number(), last: z.number() })),
    title: z.string().nullable(),
});
const toolCallDto = z.object({
    toolCallId: z.string(),
    name: z.string(),
    outcomeType: z.string(),
    failureCategory: z.string().nullable(),
    durationMs: z.number(),
    targets: z.array(entityDto),
});
const costDto = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("cost_priced"),
        rateVersion: z.number(),
        rateValidFrom: z.number(),
        inputMicroUsd: z.number(),
        outputMicroUsd: z.number(),
        cacheReadMicroUsd: z.number(),
        cacheWriteMicroUsd: z.number(),
        totalMicroUsd: z.number(),
    }),
    z.object({ type: z.literal("cost_unpriced"), reason: z.string() }),
    z.object({ type: z.literal("cost_no_usage") }),
]);
const aiUsageCallDetailDto = z.object({
    call: callRowDto,
    promptVersion: z.string(),
    firstTokenAt: z.iso.datetime({ offset: true }).nullable(),
    endedAt: z.iso.datetime({ offset: true }),
    cost: costDto,
    conversation: z
        .object({ id: z.string(), title: z.string().nullable() })
        .nullable(),
    parentToolCallId: z.string().nullable(),
    entities: z.array(entityDto),
    files: z.array(fileDto),
    toolsOffered: z.array(z.string()),
    toolCalls: z.array(toolCallDto),
    // Every attempt of the same round (chat rounds), oldest first.
    attempts: z.array(z.object({
        callId: z.string(),
        attempt: z.number(),
        outcome: aiUsageOutcomeDto,
    })),
    // The exact request/response is not kept yet (#87).
    raw: z.object({ type: z.literal("raw_not_stored") }),
});
// === Rate table ===
const aiUsageRatesDto = z.object({
    versions: z.array(z.object({
        version: z.number(),
        validFrom: z.number(),
        rates: z.array(z.object({
            modelId: z.string(),
            inputPerMtok: z.number(),
            outputPerMtok: z.number(),
            cacheReadPerMtok: z.number(),
            cacheWritePerMtok: z.number(),
        })),
    })),
    // Calls per model in the period, and whether the call was priced.
    models: z.array(z.object({
        modelId: z.string(),
        calls: z.number(),
        unpricedCalls: z.number(),
    })),
});
//# sourceMappingURL=dto.js.map